/* eslint-disable @typescript-eslint/no-explicit-any */
import { BorrowerType, quoteResidentialCommercialTypes, residentialCommercialTypes } from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { isPropertyAsset } from '@/helpers/typeGuards';
import { mapToApi } from '@/mappers';
import { mapIdsTypeToApi } from '../helpers';
import type {
  ApplicantAsset,
  IncomingProperty,
  LoanApplication,
  OutgoingProperty,
  PeriodKey,
  SupportingProperty,
} from '@/types';
import type { ApiSchema, FrequencyType } from '@/types/api';

const mapFrequency: Record<PeriodKey, FrequencyType> = {
  week: 'weekly',
  fortnight: 'fortnightly',
  month: 'monthly',
  quarter: 'quarterly',
  year: 'annually',
};

export type IncomingPropertyDto = ApiSchema['CustomerApplicationsSubmitApplicationIncomingPropertyDtoV2'];
export const incomingProperty = (data: IncomingProperty): IncomingPropertyDto => ({
  propertyType: data.propertyType,
  contractForSale: data.contractForSale,
  foundPropertyToPurchase: true,
  line1: data.address.addressLine,
  line2: data.address.addressLine2 ?? '',
  city: data.address.locality,
  state: mapToApi.australianState(data.address.state),
  postCode: data.address.postcode,
  country: 'au',
  propertyPurpose: data.propertyPurpose,
  depositAmount: data.depositAmount,
  purchasePrice: data.purchaseAmount,
  depositPaidType: data.depositType === 'cash_payment' ? 'cash_payment' : 'deposit_bond',
  settlementDate: data.settlementDate,
  stampDutyStatus: data.stampDutyStatus,
  addressManuallyCreated: data.address.addressManuallyCreated,
  rentalIncome: data.rentalIncome?.amount,
  rentalIncomeFrequency: data.rentalIncome?.frequency ? mapFrequency[data.rentalIncome?.frequency] : undefined,
  transferFeeCovered: data.transferFeeCovered,
  idsType: mapIdsTypeToApi(data.commercialType, data.idsType),
  isPropertyLeased: data.propertyLeased ?? undefined,
  ...(data.propertyLeased !== null &&
    data.propertyLeased !== undefined && {
      isPropertyLeasedApplicationValue: data.propertyLeased,
      isPropertyLeasedUsed: 'application_value',
    }),
});

export type OutgoingPropertyDto = ApiSchema['CustomerApplicationsSubmitApplicationOutgoingPropertyDtoV2'];
export const outgoingProperty = (data: OutgoingProperty): OutgoingPropertyDto => ({
  propertyType: data.propertyType,
  line1: data.address.addressLine ?? '',
  line2: data.address.addressLine2 ?? '',
  city: data.address.locality,
  state: mapToApi.australianState(data.address.state),
  postCode: data.address.postcode,
  country: 'au',
  propertyPurpose: data.propertyPurpose,
  mortgageAgainst: data.outstandingLoanAmount > 0,
  outstandingMortgageAmount: data.outstandingLoanAmount,
  salePrice: data.saleStatus === 'sold' ? data.saleAmount : 0,
  depositAmountReceived: data.depositAmount,
  settlementDate: data.settlementDate,
  depositHeldInTrust: data.depositInTrust,
  listedForSale: data.saleStatus === 'listed_for_sale',
  estimatedValuation: data.saleStatus === 'sold' ? 0 : data.saleAmount,
  soldStatus: data.saleStatus,
  addressManuallyCreated: data.address.addressManuallyCreated,
  idsType: mapIdsTypeToApi(data.commercialType, data.idsType),
  isPropertyLeased: data.propertyLeased ?? undefined,
  ...(data.propertyLeased !== null &&
    data.propertyLeased !== undefined && {
      isPropertyLeasedApplicationValue: data.propertyLeased,
      isPropertyLeasedUsed: 'application_value',
    }),
});

export const supportingProperty = (data: SupportingProperty): any => {
  const basePayload = {
    propertyType: data.propertyType,
    line1: data.address.addressLine ?? '',
    line2: data.address.addressLine2 ?? '',
    city: data.address.locality,
    state: mapToApi.australianState(data.address.state),
    postCode: data.address.postcode,
    country: 'au',
    propertyPurpose: data.propertyPurpose,
    outstandingMortgageAmount: data.outstandingLoanAmount,
    estimatedValuation: data.supportingPropertyValue,
    addressManuallyCreated: data.address.addressManuallyCreated,
    idsType: mapIdsTypeToApi(data.commercialType, data.idsType),
    isPropertyLeased: data.propertyLeased ?? undefined,
    ...(data.propertyLeased !== null &&
      data.propertyLeased !== undefined && {
        isPropertyLeasedApplicationValue: data.propertyLeased,
        isPropertyLeasedUsed: 'application_value',
      }),
    receiveRentalIncome: data.receiveRentalIncome ?? false,
    rentalIncome: data.rentalIncome?.amount,
    rentalIncomeFrequency: data.rentalIncome?.frequency ? mapFrequency[data.rentalIncome?.frequency] : undefined,
  };

  if (!useFeatureFlags().isCommercialQuoteFlowEnabled()) {
    return { ...basePayload, propertyPurpose: data.propertyPurpose };
  }

  const isResidentialCommercial =
    residentialCommercialTypes.includes(data.commercialType as (typeof residentialCommercialTypes)[number]) ||
    quoteResidentialCommercialTypes.has(data.commercialType ?? '');
  const propertyPurpose = isResidentialCommercial ? 'investment_property' : data.propertyPurpose;

  return { ...basePayload, propertyPurpose };
};

/**
 * Resolves an ownership key to an applicant ID.
 * Handles both direct applicant IDs (original flow) and borrower group IDs (commercial flow).
 * Returns null for Company/Trust groups since their ownership is stored in the notes only.
 */
const resolveOwnershipApplicantId = (ownerId: string, data: LoanApplication): string | null => {
  if (data.applicants.some((a) => a.id === ownerId)) return ownerId;

  const group = data.borrowerGroups?.find((g) => g.id === ownerId);
  if (!group || group.type !== BorrowerType.INDIVIDUAL) return null;

  return group.applicants?.[0]?.applicantId ?? null;
};

const resolveOwnershipEntries = (
  share: Record<string, number>,
  data: LoanApplication,
): { applicantIndex: number; share: number }[] =>
  Object.entries(share).flatMap(([ownerId, shareValue]) => {
    const resolvedId = resolveOwnershipApplicantId(ownerId, data);
    if (!resolvedId) return [];
    return [{ applicantIndex: mapToApi.applicantIndex(resolvedId, data.applicants), share: shareValue }];
  });

export const propertyOwnership = (
  data: LoanApplication,
): (ApiSchema['CreatePropertyOwnershipDtoV1'] | ApiSchema['CreatePropertyOwnershipDtoV2'])[] => {
  const incoming = data.incomingProperties.flatMap((p, incomingPropertyIndex) => {
    const po = data.propertiesOwnership.find((i) => i.id === p.id);
    return po
      ? resolveOwnershipEntries(po.share, data).map(({ applicantIndex, share }) => ({
          applicantIndex,
          incomingPropertyIndex,
          outgoingPropertyIndex: -1,
          assetPropertyIndex: -1,
          supportingPropertyIndex: -1,
          share,
        }))
      : [];
  });

  const outgoing = data.outgoingProperties.flatMap((p, outgoingPropertyIndex) => {
    const po = data.propertiesOwnership.find((i) => i.id === p.id);
    return po
      ? resolveOwnershipEntries(po.share, data).map(({ applicantIndex, share }) => ({
          applicantIndex,
          incomingPropertyIndex: -1,
          outgoingPropertyIndex,
          assetPropertyIndex: -1,
          supportingPropertyIndex: -1,
          share,
        }))
      : [];
  });

  const supporting = (data.supportingProperties ?? []).flatMap((p, supportingPropertyIndex) => {
    const po = data.propertiesOwnership.find((i) => i.id === p.id);
    return po
      ? resolveOwnershipEntries(po.share, data).map(({ applicantIndex, share }) => ({
          applicantIndex,
          incomingPropertyIndex: -1,
          outgoingPropertyIndex: -1,
          assetPropertyIndex: -1,
          supportingPropertyIndex,
          share,
        }))
      : [];
  });

  const assets = data.applicants.flatMap((applicant) => data.financials[applicant.id].assets);

  const assetPo = assets.filter(isPropertyAsset).flatMap((p, assetPropertyIndex) =>
    p.propertyOwnership
      ? resolveOwnershipEntries(p.propertyOwnership.share, data).map(({ applicantIndex, share }) => ({
          applicantIndex,
          incomingPropertyIndex: -1,
          outgoingPropertyIndex: -1,
          supportingPropertyIndex: -1,
          assetPropertyIndex,
          share,
        }))
      : [],
  );

  return [...incoming, ...outgoing, ...supporting, ...assetPo];
};

export const applicantDeclarationPropertyOwnership = (
  data: LoanApplication,
  assets: ApplicantAsset[],
): (ApiSchema['CreatePropertyOwnershipDtoV1'] | ApiSchema['CreatePropertyOwnershipDtoV2'])[] => {
  const assetPo = assets.filter(isPropertyAsset).flatMap((p, assetPropertyIndex) => {
    if (isPropertyAsset(p)) {
      return p.propertyOwnership
        ? Object.entries(p.propertyOwnership.share).map(([applicantId, share]) => ({
            applicantId,
            applicantIndex: mapToApi.applicantIndex(applicantId, data.applicants),
            incomingPropertyIndex: -1,
            outgoingPropertyIndex: -1,
            supportingPropertyIndex: -1,
            assetPropertyIndex,
            share,
          }))
        : [];
    } else {
      return [];
    }
  });
  return [...assetPo];
};
